import React, { useEffect, useState } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { getTranslation } from '../translations';
import { socialAPI } from '../utils/api';
import { ArrowLeft, Users, Search, UserPlus, Check, X, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const Social = () => {
  const { isKidsMode, language } = useMode();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);
  
  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'requests', 'search', 'leaderboard'
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'friends') {
      fetchFriends();
    } else if (activeTab === 'requests') {
      fetchRequests();
    } else if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [activeTab]);

  const fetchFriends = async () => {
    setLoading(true);
    try {
      const res = await socialAPI.getFriends();
      setFriends(res.data);
    } catch (error) {
      toast.error('Arkadaşlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await socialAPI.getFriendRequests();
      setRequests(res.data);
    } catch (error) {
      toast.error('İstekler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await socialAPI.getLeaderboard();
      setLeaderboard(res.data);
    } catch (error) {
      toast.error('Liderlik tablosu yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const res = await socialAPI.searchUsers(searchQuery);
      setSearchResults(res.data);
    } catch (error) {
      toast.error('Arama başarısız');
    } finally {
      setLoading(false);
    }
  };

  const sendRequest = async (userId) => {
    try {
      await socialAPI.sendFriendRequest(userId);
      toast.success('Arkadaşlık isteği gönderildi');
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await socialAPI.acceptFriendRequest(requestId);
      toast.success('Arkadaşlık kabul edildi');
      fetchRequests();
      fetchFriends();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await socialAPI.rejectFriendRequest(requestId);
      toast.success('İstek reddedildi');
      fetchRequests();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
    >
      <header className="px-6 py-4 bg-white/50 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1
            className={`text-2xl font-bold ${
              isKidsMode ? 'font-kids text-green-500' : 'font-elegant text-emerald-900'
            }`}
          >
            Sosyal
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'friends', label: 'Arkadaşlar', icon: Users },
            { id: 'requests', label: 'İstekler', icon: UserPlus },
            { id: 'search', label: 'Ara', icon: Search },
            { id: 'leaderboard', label: 'Liderlik', icon: Trophy }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
                  activeTab === tab.id
                    ? isKidsMode
                      ? 'bg-green-400 text-white rounded-full'
                      : 'bg-emerald-800 text-white rounded-md'
                    : isKidsMode
                    ? 'bg-white rounded-full text-gray-700 hover:bg-gray-100'
                    : 'bg-white rounded-md text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
          </div>
        ) : (
          <div>
            {/* Friends Tab */}
            {activeTab === 'friends' && (
              <div className="space-y-4" data-testid="friends-list">
                {friends.length === 0 ? (
                  <p className="text-center text-gray-500 py-12">Henüz arkadaşınız yok</p>
                ) : (
                  friends.map((friend) => (
                    <div
                      key={friend.id}
                      className={`p-6 flex justify-between items-center ${
                        isKidsMode
                          ? 'bg-white rounded-3xl shadow-[0_4px_0_0_rgba(0,0,0,0.1)]'
                          : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
                      }`}
                      data-testid={`friend-${friend.id}`}
                    >
                      <div>
                        <div className="font-bold text-lg">{friend.name}</div>
                        <div className="text-sm text-gray-500">
                          {friend.stats?.surahs_practiced || 0} sure - {friend.stats?.total_attempts || 0} deneme
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="space-y-4" data-testid="requests-list">
                {requests.length === 0 ? (
                  <p className="text-center text-gray-500 py-12">Arkadaşlık isteği yok</p>
                ) : (
                  requests.map((req) => (
                    <div
                      key={req.id}
                      className={`p-6 flex justify-between items-center ${
                        isKidsMode
                          ? 'bg-white rounded-3xl shadow-[0_4px_0_0_rgba(0,0,0,0.1)]'
                          : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
                      }`}
                      data-testid={`request-${req.id}`}
                    >
                      <div className="font-bold">{req.from_user?.name}</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => acceptRequest(req.id)}
                          className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                          data-testid="accept-button"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => rejectRequest(req.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          data-testid="reject-button"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Search Tab */}
            {activeTab === 'search' && (
              <div data-testid="search-section">
                <div className="flex gap-2 mb-6">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="İsim veya e-posta ile ara..."
                    className={`flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      isKidsMode
                        ? 'border-slate-200 focus:ring-green-400 rounded-2xl'
                        : 'border-stone-300 focus:ring-teal-500'
                    }`}
                    data-testid="search-input"
                  />
                  <button
                    onClick={handleSearch}
                    className={`px-6 py-3 font-medium text-white transition-all ${
                      isKidsMode
                        ? 'bg-green-400 rounded-full hover:bg-green-500'
                        : 'bg-emerald-800 rounded-md hover:bg-emerald-900'
                    }`}
                    data-testid="search-button"
                  >
                    Ara
                  </button>
                </div>

                <div className="space-y-4">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className={`p-6 flex justify-between items-center ${
                        isKidsMode
                          ? 'bg-white rounded-3xl shadow-[0_4px_0_0_rgba(0,0,0,0.1)]'
                          : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
                      }`}
                    >
                      <div>
                        <div className="font-bold">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                      <button
                        onClick={() => sendRequest(user.id)}
                        className={`px-4 py-2 font-medium text-white transition-all ${
                          isKidsMode
                            ? 'bg-pink-400 rounded-full hover:bg-pink-500'
                            : 'bg-teal-600 rounded-md hover:bg-teal-700'
                        }`}
                        data-testid="add-friend-button"
                      >
                        Arkadaş Ekle
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leaderboard Tab */}
            {activeTab === 'leaderboard' && (
              <div className="space-y-4" data-testid="leaderboard">
                {leaderboard.map((entry, idx) => (
                  <div
                    key={entry.user_id}
                    className={`p-6 flex items-center gap-4 ${
                      entry.is_current_user ? 'ring-2 ring-yellow-400' : ''
                    } ${
                      isKidsMode
                        ? 'bg-white rounded-3xl shadow-[0_4px_0_0_rgba(0,0,0,0.1)]'
                        : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
                    }`}
                    data-testid={`leaderboard-${idx}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-600' : 'bg-gray-300'
                      }`}
                    >
                      {entry.rank}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">{entry.name}</div>
                      <div className="text-sm text-gray-500">
                        {entry.score} puan - {entry.streak_days} gün seri
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Social;
